/**
 * Fire-and-forget engagement beacons for demo prospect sessions.
 *
 * The ops plane reads these per seat (sessions, hero opens, locked-run
 * attempts, booking-CTA clicks) via POST /demo/events — a whitelisted,
 * rate-limited endpoint that 403s outside demo sessions, so callers never
 * need to gate on `me.demo` themselves; failures are deliberately swallowed.
 */

export type DemoEventKind =
  | "session_start"
  | "opened_hero"
  | "locked_run"
  | "cta_click"
  | "tour_done"
  | "tour_skip"
  // One per first-run-rail state transition. The state name and the ms since
  // the arc opened ride in `context` (`state` / `ms`), so the whole 0-90s arc
  // is one kind rather than seven — see components/demo/firstRun/. The
  // terminal `state: "done"` doubles as the seat's server-side play-once mark.
  | "first_run"
  /** The prospect ingested one of their OWN files — participation, not
   *  spectating. Fired twice per file: `phase: "dropped"` then `"ready"`
   *  (with `ms`). The server whitelists both the kind and these context keys. */
  | "file_drop"
  // One per edit-a-number recompute, carrying the value the prospect dragged
  // to (`value`) and what the recompose actually cost (`ms`).
  | "ripple";

/** Whitelisted server-side: `path`, `workspace_id`, `phase`, `ext`, `state` (strings)
 *  and `ms` / `value` (numbers). Anything else is dropped. */
export type DemoEventContext = Record<string, string | number>;

export function postDemoEvent(kind: DemoEventKind, context: DemoEventContext = {}): void {
  try {
    void fetch("/api/demo/events", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind, context: { path: window.location.pathname, ...context } }),
    }).catch(() => undefined);
  } catch {
    /* never let a beacon break the app */
  }
}
